/**
 * The hub's Booking tab (docs/ux/clinic.md): the sentence about VitalSign,
 * the booking (or rebook) action the header also holds, the appointments
 * with their marks and a Confirmed switch, and the side-steps that matter
 * at the door, reused from the Overview.
 */

import { statusLabel } from '@beam/core/status';
import type { StatusEvent } from '@beam/core/status';
import type { MatterDetail, SideStepKey, TransitionOption } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ATTENDANCE_WORD, confirmedWord } from '@/features/clinic/describe';
import { dateTime } from '@/lib/format';
import { Panel, SideStepsChecklist } from '../panels';
import { useConfirmAppointment } from '../useMatter';

const SENTENCE = 'VitalSign is the source of appointments; the appointment is keyed here today.';

export function BookingTab({ matterRef, detail, offline, onEvent, busyEvent, pendingStep, onSideStep }: {
  matterRef: string;
  detail: MatterDetail | undefined;
  offline: boolean;
  onEvent: (option: TransitionOption) => void;
  busyEvent: StatusEvent['kind'] | null;
  pendingStep: SideStepKey | null;
  onSideStep: (key: SideStepKey, value: boolean, label: string) => void;
}) {
  const phone = useIsPhone();
  const confirm = useConfirmAppointment(matterRef);
  if (!detail) return null;
  const status = detail.matter.status;
  const booking = detail.options.find((o) => o.kind === (status === 14 ? 'rebook' : 'appointment_booked'));
  const noAppointment = detail.options.find((o) => o.kind === 'no_appointment_required');
  const bookable = status === 4 || status === 14;
  const appointments = [...detail.appointments].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="flex flex-col gap-6">
        <Panel title="Booking">
          <div className="flex flex-col gap-3">
            <p className="text-sm text-beam-text-muted">{SENTENCE}</p>
            {status < 4 ? (
              <p className="text-sm">Booking opens at 4 {statusLabel(4)}; the case is at {status} {statusLabel(status)}.</p>
            ) : bookable && booking ? (
              booking.allowed ? (
                <Button data-slot="book" data-primary="true" size={phone ? 'lg' : 'default'} className={phone ? 'h-11 w-full' : 'self-start'} disabled={offline || busyEvent !== null} onClick={() => onEvent(booking)}>
                  {status === 14 ? 'Rebook' : 'Book appointment'}
                </Button>
              ) : (
                <p className="text-sm" data-slot="booking-refused">{booking.reason}{noAppointment?.allowed ? ' The header offers "No appointment required".' : ''}</p>
              )
            ) : null}
          </div>
        </Panel>
        <Panel title="Appointments">
          {appointments.length === 0 ? <p className="text-sm text-beam-text-muted">No appointment recorded.</p> : (
            <ul className="flex flex-col divide-y divide-beam-line" data-slot="appointments">
              {appointments.map((a) => (
                <li key={a.apptId} data-appt={a.apptId} className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between md:gap-4">
                  <span className="flex flex-col leading-tight">
                    <span className="tabular text-sm font-medium">{dateTime(a.startsAt)}</span>
                    <span className="text-[13px] text-beam-text-muted">{a.clinicName}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-3">
                    {a.attendance ? (
                      <span className={a.attendance === 'attended' ? 'text-sm font-medium' : 'text-sm font-medium text-beam-error'}>{ATTENDANCE_WORD[a.attendance]}</span>
                    ) : (
                      <label className="flex min-h-11 items-center gap-2 text-sm">
                        <Switch checked={a.confirmed} disabled={offline || confirm.isPending} aria-label={`Confirmed, ${dateTime(a.startsAt)}`} onCheckedChange={(c) => confirm.mutate({ apptId: a.apptId, confirmed: c })} />
                        <span data-slot="confirmed-word">{confirmedWord(a.confirmed)}</span>
                      </label>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <div className="flex flex-col gap-6">
        <SideStepsChecklist sideSteps={detail.cycle.sideSteps} offline={offline} pendingKey={pendingStep} onSet={onSideStep} />
      </div>
    </div>
  );
}
